import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Plus, Trash2, Play, Save, ChevronDown, ChevronRight, X, FolderOpen, Sigma, Link2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { exportToCSV } from '../../utils/exportCSV';

const OPERATORS = [
  { value: 'equals',       label: 'equals' },
  { value: 'not_equals',   label: 'does not equal' },
  { value: 'contains',     label: 'contains' },
  { value: 'starts_with',  label: 'starts with' },
  { value: 'greater_than', label: 'greater than' },
  { value: 'less_than',    label: 'less than' },
  { value: 'between',      label: 'between' },
  { value: 'is_empty',     label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

const LIMIT_OPTIONS = [
  { value: 100,  label: '100 rows' },
  { value: 500,  label: '500 rows' },
  { value: 1000, label: '1,000 rows' },
  { value: 2000, label: '2,000 rows' },
];

const fmt = (v, type) => {
  if (v === null || v === undefined) return '—';
  if (type === 'currency' && typeof v === 'number') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v);
  }
  if (type === 'boolean') return v ? 'Yes' : 'No';
  if (type === 'datetime' && v) {
    try { return new Date(v).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return String(v); }
  }
  if (type === 'date' && v) {
    try {
      const d = new Date(v);
      if (!isNaN(d.getTime())) return d.toLocaleDateString();
      const d2 = new Date(v + 'T12:00:00');
      if (!isNaN(d2.getTime())) return d2.toLocaleDateString();
    } catch {}
    return String(v);
  }
  return String(v);
};

const CustomReportBuilder = ({ onApprove }) => {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [source, setSource] = useState('');
  const [sourceFields, setSourceFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [filters, setFilters] = useState([]);
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [limit, setLimit] = useState(500);
  const [reportName, setReportName] = useState('');
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [savedQueries, setSavedQueries] = useState([]);
  const [loadOpen, setLoadOpen] = useState(false);
  const [showTotals, setShowTotals] = useState(true);
  // Cross-source enrichment
  const [enrichSource, setEnrichSource] = useState('');       // secondary source key
  const [enrichFields, setEnrichFields] = useState([]);       // selected fields from secondary
  const [enrichFieldsOpen, setEnrichFieldsOpen] = useState(true);
  // Owner filter options: CRM accounts + Consignor tenant
  const [ownerOptions, setOwnerOptions] = useState([]);

  // Derived: available joins for current primary source
  const availableJoins = useMemo(() => {
    if (!source) return [];
    const src = sources.find(s => s.key === source);
    return src?.joins || [];
  }, [source, sources]);

  // Derived: fields of the selected secondary source
  const enrichSourceFields = useMemo(() => {
    if (!enrichSource) return [];
    const src = sources.find(s => s.key === enrichSource);
    // Exclude "related" (aggregated) fields — they don't work in cross-joins
    return (src?.fields || []).filter(f => !f.related);
  }, [enrichSource, sources]);

  useEffect(() => {
    api.get('/reports/custom/sources').then(r => setSources(r.data || [])).catch(() => {});
    api.get('/reports/saved').then(r => {
      const customs = (r.data || []).filter(rpt => rpt.report_type === 'custom' && rpt.filters?.source);
      setSavedQueries(customs);
    }).catch(() => {});
    // Load owner options: CRM accounts + Consignor tenant
    const userStr = localStorage.getItem('user');
    const tenantId = userStr ? JSON.parse(userStr).tenant_id : null;
    Promise.all([
      api.get('/crm-entities?crm_class=account'),
      tenantId ? api.get(`/tenants/${tenantId}`).catch(() => null) : Promise.resolve(null),
    ]).then(([crmRes, tenantRes]) => {
      const opts = (crmRes.data || []).map(e => ({ id: e.id, name: e.system_name || e.name || '' }));
      if (tenantRes?.data?.id) {
        opts.push({ id: tenantRes.data.id, name: tenantRes.data.name + ' (Consignor)' });
      }
      setOwnerOptions(opts);
    }).catch(() => {});
  }, []);

  // Compute summary totals for numeric/currency columns (never sum hip_number)
  const NO_SUM_KEYS = new Set(['hip_number', '_x_hip_number']);
  const summaryRow = useMemo(() => {
    if (!results?.rows?.length) return null;
    const cols = results.columns || [];
    const numericCols = cols.filter(c => (c.type === 'currency' || c.type === 'number') && !NO_SUM_KEYS.has(c.key));
    if (!numericCols.length) return null;
    const totals = {};
    numericCols.forEach(col => {
      totals[col.key] = results.rows.reduce((s, r) => s + (Number(r[col.key]) || 0), 0);
    });
    return totals;
  }, [results]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSourceChange = (s) => {
    setSource(s);
    const src = sources.find(x => x.key === s);
    const fields = src?.fields || [];
    // Keep first 5 default fields in place; sort the rest alphabetically
    const defaultFields = fields.slice(0, 5);
    const rest = fields.slice(5).sort((a, b) => a.label.localeCompare(b.label));
    const sortedFields = [...defaultFields, ...rest];
    setSourceFields(sortedFields);
    setSelectedFields(sortedFields.slice(0, 5).map(f => f.key));
    setFilters([]);
    setSortBy('');
    setResults(null);
    // Reset enrichment when primary source changes
    setEnrichSource('');
    setEnrichFields([]);
  };

  const handleLoadSavedQuery = (savedReport) => {
    const cfg = savedReport.filters || {};
    const srcKey = cfg.source;
    if (!srcKey) return;
    const src = sources.find(x => x.key === srcKey);
    const fields = src?.fields || [];
    // Keep first 5 default fields in place; sort the rest alphabetically
    const defaultFields = fields.slice(0, 5);
    const rest = fields.slice(5).sort((a, b) => a.label.localeCompare(b.label));
    const sortedFields = [...defaultFields, ...rest];
    setSource(srcKey);
    setSourceFields(sortedFields);
    setSelectedFields(cfg.fields || []);
    setFilters((cfg.filters || []).map(f => ({ ...f, id: Date.now() + Math.random() })));
    setSortBy(cfg.sort_by || '');
    setSortDir(cfg.sort_dir || 'asc');
    setEnrichSource(cfg.enrich_source || '');
    setEnrichFields(cfg.enrich_fields || []);
    setReportName(savedReport.report_name || '');
    setResults(null);
    setLoadOpen(false);
    toast.success(`Loaded: ${savedReport.report_name}`);
  };

  const toggleField = (key) => {
    setSelectedFields(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const addFilter = () => {
    setFilters(prev => [...prev, { id: Date.now(), field: sourceFields[0]?.key || '_pick_', op: 'equals', value: '' }]);
  };

  const updateFilter = (id, patch) => {
    setFilters(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));
  };

  const removeFilter = (id) => setFilters(prev => prev.filter(f => f.id !== id));

  const handleRun = async () => {
    if (!source) { toast.error('Select a data source'); return; }
    if (!selectedFields.length) { toast.error('Select at least one field'); return; }
    setRunning(true);
    try {
      const res = await api.post('/reports/custom/run', {
        source, fields: selectedFields,
        filters: filters.map(({ id, ...rest }) => rest),
        sort_by: sortBy, sort_dir: sortDir,
        limit,
        enrich_source: enrichSource || undefined,
        enrich_fields: enrichFields.length ? enrichFields : undefined,
      });
      setResults(res.data);
      if (!reportName) {
        const src = sources.find(x => x.key === source);
        const enrichLabel = enrichSource ? ` + ${sources.find(x => x.key === enrichSource)?.label || ''}` : '';
        setReportName(`Custom ${src?.label || source}${enrichLabel} Report`);
      }
    } catch { toast.error('Failed to run report'); }
    setRunning(false);
  };

  const handleDownloadCSV = () => {
    if (!results) return;
    exportToCSV(
      results.rows,
      results.columns.map(c => ({ key: c.key, label: c.label, accessor: r => fmt(r[c.key], c.type) })),
      reportName || 'custom-report'
    );
  };

  const handleDownloadPDF = async () => {
    if (!results) return;
    // Save first, then download PDF
    setSaving(true);
    try {
      const res = await api.post('/reports/save', {
        report_type: 'custom',
        report_name: reportName || 'Custom Report',
        report_data: results,
        columns: results.columns,
        row_count: results.total,
        filters: { source, fields: selectedFields, filters: filters.map(({ id, ...rest }) => rest), sort_by: sortBy, sort_dir: sortDir, enrich_source: enrichSource || undefined, enrich_fields: enrichFields.length ? enrichFields : undefined },
        report_date: new Date().toLocaleDateString('en-CA'),
      });
      const reportId = res.data?.id;
      if (reportId) {
        const pdfRes = await api.get(`/reports/saved/${reportId}/pdf`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
        const a = document.createElement('a'); a.href = url;
        a.download = `${(reportName || 'Report').replace(/\s+/g, '-')}.pdf`;
        a.click();
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
        toast.success('PDF downloaded');
      }
    } catch { toast.error('Failed to download PDF'); }
    setSaving(false);
  };

  const handleApprove = async () => {
    if (!results) return;
    setSaving(true);
    try {
      const res = await api.post('/reports/save', {
        report_type: 'custom',
        report_name: reportName || 'Custom Report',
        report_data: results,
        columns: results.columns,
        row_count: results.total,
        filters: { source, fields: selectedFields, filters: filters.map(({ id, ...rest }) => rest), sort_by: sortBy, sort_dir: sortDir, enrich_source: enrichSource || undefined, enrich_fields: enrichFields.length ? enrichFields : undefined },
        report_date: new Date().toLocaleDateString('en-CA'),
      });
      toast.success('Report approved');
      if (onApprove) onApprove();
      navigate(`/reports/saved/${res.data?.id}`);
    } catch { toast.error('Failed to approve report'); }
    setSaving(false);
  };

  const handleClear = () => {
    setSource('');
    setSourceFields([]);
    setSelectedFields([]);
    setFilters([]);
    setSortBy('');
    setSortDir('asc');
    setLimit(500);
    setReportName('');
    setResults(null);
    setEnrichSource('');
    setEnrichFields([]);
  };
  const columns = results?.columns || [];
  const rows = results?.rows || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Custom Report Builder</h3>
        <div className="flex items-center gap-2">
          {savedQueries.length > 0 && (
            <div className="relative">
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1"
                onClick={() => setLoadOpen(o => !o)}>
                <FolderOpen className="w-3.5 h-3.5" /> Load Saved
                <ChevronDown className="w-3 h-3" />
              </Button>
              {loadOpen && (
                <div className="absolute right-0 top-9 z-50 w-72 bg-background border rounded-md shadow-lg max-h-64 overflow-y-auto">
                  {savedQueries.map(q => (
                    <button key={q.id}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 border-b last:border-0"
                      onClick={() => handleLoadSavedQuery(q)}>
                      <p className="font-medium truncate">{q.report_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {q.filters?.source} · {new Date(q.saved_at).toLocaleDateString()}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {(source || reportName) && (
            <Button variant="ghost" size="sm" className="text-xs text-muted-foreground h-8" onClick={handleClear}>
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Click-outside handler for load dropdown */}
      {loadOpen && <div className="fixed inset-0 z-40" onClick={() => setLoadOpen(false)} />}
      {/* Report Name + Source + Enrich */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Report Name</Label>
          <Input value={reportName} onChange={e => setReportName(e.target.value)} placeholder="My Custom Report" />
        </div>
        <div className="space-y-2">
          <Label>Primary Data Source</Label>
          <Select value={source} onValueChange={handleSourceChange}>
            <SelectTrigger><SelectValue placeholder="Select data source..." /></SelectTrigger>
            <SelectContent>
              {sources.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Cross-reference secondary source */}
      {source && availableJoins.length > 0 && (
        <div className="flex items-start gap-3 p-3 rounded-md border bg-muted/20">
          <Link2 className="w-4 h-4 mt-1.5 text-muted-foreground shrink-0" />
          <div className="flex-1 space-y-1">
            <Label className="text-sm">Also include data from</Label>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={enrichSource || '_none_'} onValueChange={v => {
                if (v === '_none_') { setEnrichSource(''); setEnrichFields([]); }
                else { setEnrichSource(v); setEnrichFields([]); }
              }}>
                <SelectTrigger className="w-56 h-8 text-sm">
                  <SelectValue placeholder="None — single source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none_">None — single source</SelectItem>
                  {availableJoins.map(j => (
                    <SelectItem key={j.key} value={j.key}>{j.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {enrichSource && (
                <>
                  <span className="text-xs text-muted-foreground">
                    {availableJoins.find(j => j.key === enrichSource)?.description}
                  </span>
                  <button onClick={() => { setEnrichSource(''); setEnrichFields([]); }}
                    className="ml-auto text-muted-foreground hover:text-foreground">
                    <XCircle className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {source && (
        <>
          {/* Fields */}
          <Card>
            <CardHeader className="py-2 px-4 cursor-pointer" onClick={() => setFieldsOpen(o => !o)}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">
                  Fields <span className="text-muted-foreground font-normal">({selectedFields.length + enrichFields.length} selected)</span>
                </CardTitle>
                {fieldsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </div>
            </CardHeader>
            {fieldsOpen && (
              <CardContent className="pt-0 pb-3 space-y-3">
                {/* Primary source fields */}
                <div>
                  {enrichSource && (
                    <p className="text-xs font-medium text-muted-foreground mb-1 uppercase tracking-wide">
                      {sources.find(s => s.key === source)?.label}
                    </p>
                  )}
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                    {sourceFields.map(f => (
                      <label key={f.key} className="flex items-center gap-2 cursor-pointer text-sm hover:bg-muted/30 rounded px-2 py-1">
                        <Checkbox checked={selectedFields.includes(f.key)} onCheckedChange={() => toggleField(f.key)} />
                        <span>{f.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button variant="link" size="sm" className="h-auto p-0 text-xs"
                      onClick={() => setSelectedFields(sourceFields.map(f => f.key))}>Select All</Button>
                    <Button variant="link" size="sm" className="h-auto p-0 text-xs"
                      onClick={() => setSelectedFields([])}>Clear All</Button>
                  </div>
                </div>

                {/* Enriched source fields */}
                {enrichSource && enrichSourceFields.length > 0 && (
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                        <Link2 className="w-3 h-3" />
                        {sources.find(s => s.key === enrichSource)?.label}
                      </p>
                      <div className="flex gap-2">
                        <Button variant="link" size="sm" className="h-auto p-0 text-xs"
                          onClick={() => setEnrichFields(enrichSourceFields.map(f => f.key))}>Select All</Button>
                        <Button variant="link" size="sm" className="h-auto p-0 text-xs"
                          onClick={() => setEnrichFields([])}>Clear All</Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                      {enrichSourceFields.map(f => (
                        <label key={f.key} className="flex items-center gap-2 cursor-pointer text-sm hover:bg-muted/30 rounded px-2 py-1">
                          <Checkbox
                            checked={enrichFields.includes(f.key)}
                            onCheckedChange={() => setEnrichFields(prev =>
                              prev.includes(f.key) ? prev.filter(k => k !== f.key) : [...prev, f.key]
                            )}
                          />
                          <span className="text-muted-foreground">{f.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Filters */}
          <Card>
            <CardHeader className="py-2 px-4 cursor-pointer" onClick={() => setFiltersOpen(o => !o)}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Filters <span className="text-muted-foreground font-normal">({filters.length})</span></CardTitle>
                {filtersOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </div>
            </CardHeader>
            {filtersOpen && (
              <CardContent className="pt-0 pb-3 space-y-2">
                {filters.map(f => (
                  <div key={f.id} className="flex gap-2 items-center flex-wrap">
                    <Select value={f.field} onValueChange={v => updateFilter(f.id, { field: v })}>
                      <SelectTrigger className="w-44 h-8 text-sm"><SelectValue placeholder="Field" /></SelectTrigger>
                      <SelectContent>{sourceFields.map(sf => <SelectItem key={sf.key} value={sf.key}>{sf.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={f.op} onValueChange={v => updateFilter(f.id, { op: v })}>
                      <SelectTrigger className="w-36 h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{OPERATORS.map(op => <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>)}</SelectContent>
                    </Select>
                    {(() => {
                      const fieldType = sourceFields.find(sf => sf.key === f.field)?.type;
                      if (fieldType === 'owner' && !['is_empty','is_not_empty'].includes(f.op)) {
                        return (
                          <Select value={f.value || ''} onValueChange={v => updateFilter(f.id, { value: v })}>
                            <SelectTrigger className="w-52 h-8 text-sm"><SelectValue placeholder="Select owner..." /></SelectTrigger>
                            <SelectContent>
                              {ownerOptions.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        );
                      }
                      if (f.op === 'between') return (
                        <>
                          <Input className="w-28 h-8 text-sm" value={f.value || ''} placeholder="From"
                            onChange={e => updateFilter(f.id, { value: e.target.value })} />
                          <span className="text-xs text-muted-foreground">and</span>
                          <Input className="w-28 h-8 text-sm" value={f.value2 || ''} placeholder="To"
                            onChange={e => updateFilter(f.id, { value2: e.target.value })} />
                        </>
                      );
                      if (!['is_empty', 'is_not_empty'].includes(f.op)) return (
                        <Input className="w-36 h-8 text-sm" value={f.value}
                          onChange={e => updateFilter(f.id, { value: e.target.value })} placeholder="Value" />
                      );
                      return null;
                    })()}
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600"
                      onClick={() => removeFilter(f.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={addFilter}>
                  <Plus className="w-3 h-3 mr-1" /> Add Filter
                </Button>
              </CardContent>
            )}
          </Card>

          {/* Sort + Limit + Run */}
          <div className="flex gap-3 items-end flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Sort by</Label>
              <Select value={sortBy || '_none_'} onValueChange={v => setSortBy(v === '_none_' ? '' : v)}>
                <SelectTrigger className="w-44 h-8 text-sm"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none_">None</SelectItem>
                  {sourceFields.filter(f => selectedFields.includes(f.key)).map(f =>
                    <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            {sortBy && (
              <Select value={sortDir} onValueChange={setSortDir}>
                <SelectTrigger className="w-28 h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">A → Z</SelectItem>
                  <SelectItem value="desc">Z → A</SelectItem>
                </SelectContent>
              </Select>
            )}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Row limit</Label>
              <Select value={String(limit)} onValueChange={v => setLimit(Number(v))}>
                <SelectTrigger className="w-32 h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LIMIT_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleRun} disabled={running} className="h-8 bg-primary hover:bg-primary/90 text-white">
              <Play className="w-3.5 h-3.5 mr-1" /> {running ? 'Running...' : 'Run Report'}
            </Button>
          </div>
        </>
      )}

      {/* Results */}
      {results && (
        <Card>
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm">
                Results <Badge variant="outline" className="ml-2 text-xs">{results.total} rows</Badge>
                {summaryRow && (
                  <button className="ml-3 text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                    onClick={() => setShowTotals(t => !t)}>
                    <Sigma className="w-3 h-3" /> {showTotals ? 'Hide totals' : 'Show totals'}
                  </button>
                )}
              </CardTitle>
              <div className="flex gap-2">
                <Button size="sm" className="h-8 text-xs bg-primary hover:bg-primary/90 text-white"
                  onClick={handleApprove} disabled={saving}>
                  <Save className="w-3 h-3 mr-1" /> {saving ? 'Saving...' : 'Approve Report'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No results match your criteria.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    {columns.map(c => <TableHead key={c.key} className="text-xs">{c.label}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.slice(0, 200).map((row, i) => (
                    <TableRow key={i} className="hover:bg-muted/20">
                      {columns.map(c => (
                        <TableCell key={c.key} className="text-sm">
                          {fmt(row[c.key], c.type)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {summaryRow && showTotals && (
                    <TableRow className="bg-muted/40 font-semibold border-t-2">
                      {columns.map((c, i) => (
                        <TableCell key={c.key} className="text-sm">
                          {i === 0 ? (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Sigma className="w-3 h-3" /> Totals
                            </span>
                          ) : summaryRow[c.key] !== undefined ? (
                            fmt(summaryRow[c.key], c.type)
                          ) : ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
            {rows.length > 200 && (
              <p className="text-xs text-muted-foreground text-center py-2">Showing first 200 of {rows.length} rows — download CSV for full data</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CustomReportBuilder;
